import React, { useEffect, useState, useMemo, useRef, useCallback } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import api from '../services/api';
import { getImageUrl } from '../utils/imageUrl';
import SEO from '../components/SEO';
import { ProductCard, Button, Skeleton } from '../components/primitives';
import { Icon, ICON_DEFAULTS } from '../utils/icons';
import SectionRenderer from '../components/sections/SectionRenderer';
import { HOME_SECTION_REGISTRY } from '../components/sections/homeSectionRegistry';
import { useSiteSettings } from '../context/SiteSettingsContext';
import './CategoryLanding.css';

const IMAGE_INTERVAL_MS = 3000;

const CategoryLanding = () => {
  const { slug } = useParams();
  const { t, i18n } = useTranslation();
  const settings = useSiteSettings();

  const [category, setCategory] = useState(null);
  const [carouselItems, setCarouselItems] = useState([]);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentSlide, setCurrentSlide] = useState(0);
  const [selectedTags, setSelectedTags] = useState([]);
  const [isMuted, setIsMuted] = useState(true);
  const [resourceTemplate, setResourceTemplate] = useState(null);
  const [openFaqKey, setOpenFaqKey] = useState(null);
  const [newsletterEmail, setNewsletterEmail] = useState('');
  const [newsletterMessage, setNewsletterMessage] = useState('');
  const [newsletterSubmitting, setNewsletterSubmitting] = useState(false);
  const isEditorPreview = new URLSearchParams(window.location.search).get('themePreview') === '1';
  const timerRef = useRef(null);
  const videoRefs = useRef({});

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const [catRes, carouselRes, prodRes] = await Promise.all([
          api.get(`/api/categories/${slug}`),
          api.get(`/api/carousels?category=${slug}`),
          api.get(`/api/products?categorySlug=${slug}&limit=100`),
        ]);
        const categoryData = catRes.data.category || catRes.data;
        setCategory(categoryData);
        api.get(`/api/themes/templates/${categoryData.themeTemplateKey || 'collection.default'}`)
          .then((response) => setResourceTemplate(response.data))
          .catch(() => setResourceTemplate(null));
        const rawItems = carouselRes.data.items || carouselRes.data.carousels || [];
        setCarouselItems(rawItems);
        setProducts(prodRes.data.products || []);
      } catch (err) {
        console.error('Error fetching category data:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slug, i18n.language]);

  useEffect(() => {
    if (!isEditorPreview) return undefined;
    const handleLayoutPreview = (event) => {
      if (event.origin !== window.location.origin || event.data?.type !== 'STOREFRONT_LAYOUT_PREVIEW') return;
      setResourceTemplate(event.data.payload);
    };
    window.addEventListener('message', handleLayoutPreview);
    return () => window.removeEventListener('message', handleLayoutPreview);
  }, [isEditorPreview]);

  const advanceSlide = useCallback(() => {
    setCurrentSlide((prev) => (prev + 1) % carouselItems.length);
  }, [carouselItems.length]);

  const scheduleNext = useCallback((currentItem) => {
    if (timerRef.current) clearTimeout(timerRef.current);

    if (!currentItem || currentItem.mediaType !== 'video') {
      timerRef.current = setTimeout(advanceSlide, IMAGE_INTERVAL_MS);
    }
  }, [advanceSlide]);

  useEffect(() => {
    if (carouselItems.length <= 1) return;

    const current = carouselItems[currentSlide];
    scheduleNext(current);

    carouselItems.forEach((item, idx) => {
      const vid = videoRefs.current[idx];
      if (!vid) return;
      if (idx === currentSlide && item.mediaType === 'video') {
        vid.currentTime = 0;
        vid.play().catch(() => {});
      } else {
        vid.pause();
      }
    });

    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, [currentSlide, carouselItems, scheduleNext]);

  const handleVideoEnded = useCallback(() => {
    if (carouselItems.length > 1) advanceSlide();
  }, [carouselItems.length, advanceSlide]);

  const goToPrev = useCallback(() => {
    if (timerRef.current) clearTimeout(timerRef.current);
    setCurrentSlide((prev) => (prev - 1 + carouselItems.length) % carouselItems.length);
  }, [carouselItems.length]);

  const goToNext = useCallback(() => {
    if (timerRef.current) clearTimeout(timerRef.current);
    advanceSlide();
  }, [advanceSlide]);

  const goToSlide = useCallback((idx) => {
    if (timerRef.current) clearTimeout(timerRef.current);
    setCurrentSlide(idx);
  }, []);

  const toggleMute = useCallback(() => {
    setIsMuted((prev) => {
      const next = !prev;
      Object.values(videoRefs.current).forEach((vid) => {
        if (vid) vid.muted = next;
      });
      return next;
    });
  }, []);

  const allTags = useMemo(() => {
    const tagSet = new Set();
    products.forEach((p) => {
      if (Array.isArray(p.tags)) {
        p.tags.forEach((tag) => tagSet.add(tag));
      }
    });
    return Array.from(tagSet).sort();
  }, [products]);

  const filteredProducts = useMemo(() => {
    if (selectedTags.length === 0) return products;
    return products.filter((p) =>
      Array.isArray(p.tags) && selectedTags.every((t) => p.tags.includes(t))
    );
  }, [products, selectedTags]);

  const toggleTag = (tag) => {
    setSelectedTags((prev) =>
      prev.includes(tag) ? prev.filter((t) => t !== tag) : [...prev, tag]
    );
  };

  const handleNewsletterSubmit = async (event) => {
    event.preventDefault();
    if (!newsletterEmail.trim()) return;
    setNewsletterSubmitting(true);
    setNewsletterMessage('');
    try {
      await api.post('/api/newsletter/subscribe', { email: newsletterEmail.trim(), source: `collection:${slug}`, language: i18n.language || 'sq' });
      setNewsletterMessage(t('home.newsletter.success', 'Thanks, you are on the list.'));
      setNewsletterEmail('');
    } catch {
      setNewsletterMessage(t('home.newsletter.error', 'Could not subscribe. Please try again later.'));
    } finally {
      setNewsletterSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="category-landing" aria-busy="true">
        <div className="container">
          <div style={{ padding: 'var(--space-8) 0' }}>
            <Skeleton.Block height="2.5rem" width="40%" rounded="md" style={{ marginBottom: 'var(--space-4)' }} />
            <Skeleton.Line width="70%" />
            <Skeleton.Line width="55%" />
            <div className="products-grid" style={{ marginTop: 'var(--space-6)' }}>
              {Array.from({ length: 8 }).map((_, i) => (
                <Skeleton.Card key={i} />
              ))}
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!category) {
    return (
      <div className="category-landing-empty">
        <h2>Category not found</h2>
        <Link to="/products">Browse all products</Link>
      </div>
    );
  }

  const templateSections = resourceTemplate?.template?.sections || [
    { type: 'collection_hero', enabled: true, settings: { style: 'standard' } },
    { type: 'collection_products', enabled: true, settings: { showTagFilters: true } },
  ];
  const heroRegion = templateSections.find((section) => section.type === 'collection_hero');
  const productsRegion = templateSections.find((section) => section.type === 'collection_products');
  const contentSections = templateSections.filter((section) => !['collection_hero', 'collection_products'].includes(section.type));
  const heroEnabled = heroRegion?.enabled !== false && !!heroRegion;
  const productsEnabled = productsRegion?.enabled !== false && !!productsRegion;
  const campaignStyle = heroRegion?.settings?.style === 'immersive';
  const collectionContext = {
    t,
    i18n,
    currency: settings.currency,
    storeName: settings.storeName,
    trustItems: [],
    trustVars: () => ({}),
    heroCollage: [],
    hasCollage: false,
    featuredProducts: products,
    categories: [category],
    bundles: [],
    recentlyViewed: [],
    openFaqKey,
    setOpenFaqKey,
    newsletterEmail,
    setNewsletterEmail,
    newsletterMessage,
    newsletterSubmitting,
    handleNewsletterSubmit,
    isEditorPreview,
    dynamicSources: {
      collection: category,
      store: {
        name: settings.storeName,
        email: settings.storeEmail,
        phone: settings.storePhone,
        address: settings.storeAddress,
        nipt: settings.storeNipt,
        whatsAppPhone: settings.whatsAppPhone,
        currency: settings.currency,
      },
    },
    contact: {
      email: settings.storeEmail,
      phone: settings.storePhone,
      address: settings.storeAddress,
      nipt: settings.storeNipt,
      whatsAppEnabled: settings.whatsAppEnabled,
      whatsAppPhone: settings.whatsAppPhone,
      mapLat: settings.storeMapLat,
      mapLng: settings.storeMapLng,
    },
  };

  const heroContent = heroEnabled && carouselItems.length > 0 ? (
    <div className="cl-carousel">
      {carouselItems.map((item, idx) => (
        <div key={item.id || idx} className={`cl-carousel-slide ${idx === currentSlide ? 'active' : ''}`}>
          {item.mediaType === 'video' ? (
            <video ref={(el) => { videoRefs.current[idx] = el; }} src={getImageUrl(item.mediaUrl || item.url)} muted={isMuted} playsInline preload="auto" onEnded={handleVideoEnded} />
          ) : (
            <img src={getImageUrl(item.mediaUrl || item.imageUrl || item.url, { size: 'carousel' })} alt={item.title || category.name} onError={(event) => { event.target.src = getImageUrl(item.mediaUrl || item.imageUrl || item.url); }} />
          )}
        </div>
      ))}
      <div className="cl-carousel-overlay" />
      {carouselItems.length > 1 && <>
        <button className="cl-carousel-arrow cl-carousel-arrow-prev" onClick={goToPrev} aria-label={t('common.previous', 'Previous slide')}><Icon.ChevronLeft size={24} strokeWidth={2.5} aria-hidden="true" /></button>
        <button className="cl-carousel-arrow cl-carousel-arrow-next" onClick={goToNext} aria-label={t('common.next', 'Next slide')}><Icon.ChevronRight size={24} strokeWidth={2.5} aria-hidden="true" /></button>
        {carouselItems[currentSlide]?.mediaType === 'video' && <>
          <button className="cl-carousel-mute" onClick={toggleMute} aria-label={isMuted ? t('common.unmute', 'Unmute') : t('common.mute', 'Mute')}>{isMuted ? <Icon.VolumeMute size={20} strokeWidth={ICON_DEFAULTS.strokeWidth} aria-hidden="true" /> : <Icon.Volume size={20} strokeWidth={ICON_DEFAULTS.strokeWidth} aria-hidden="true" />}</button>
          <button className="cl-carousel-skip" onClick={goToNext} aria-label={t('common.skipVideo', 'Skip video')}>{t('common.skip', 'Skip')} <Icon.ChevronRight size={16} strokeWidth={2.25} aria-hidden="true" /></button>
        </>}
        <div className="cl-carousel-progress">{carouselItems.map((item, idx) => <button key={idx} className={`cl-carousel-progress-seg${idx === currentSlide ? ' active' : ''}${idx < currentSlide ? ' visited' : ''}${item.mediaType === 'video' ? ' video' : ''}`} onClick={() => goToSlide(idx)} aria-label={`Go to slide ${idx + 1}`} />)}</div>
      </>}
    </div>
  ) : null;

  const productsContent = (
    <div className="container">
      <div className="cl-header"><h1 className="cl-title">{category.name}</h1>{category.description && <p className="cl-description">{category.description}</p>}</div>
      {productsEnabled && productsRegion?.settings?.showTagFilters !== false && allTags.length > 0 && <div className="cl-tags-row"><button className={`cl-tag-pill ${selectedTags.length === 0 ? 'active' : ''}`} onClick={() => setSelectedTags([])}>{t('common.all', 'All')}</button>{allTags.map((tag) => <button key={tag} className={`cl-tag-pill ${selectedTags.includes(tag) ? 'active' : ''}`} onClick={() => toggleTag(tag)}>{t(`tags.${tag}`, tag.charAt(0).toUpperCase() + tag.slice(1).replace(/-/g, ' '))}</button>)}</div>}
      {productsEnabled && (filteredProducts.length > 0 ? <div className="cl-products-grid">{filteredProducts.map((product) => <ProductCard key={product.id} product={product} />)}</div> : <div className="no-products"><h2>{t('products.noProducts', 'No products found')}</h2><p>{t('products.noProductsMessage', 'Try adjusting your filters.')}</p><Button variant="brand" onClick={() => setSelectedTags([])}>{t('products.clearAll', 'Clear filters')}</Button></div>)}
    </div>
  );

  const templateRegions = [
    heroContent && { key: heroRegion.key || 'collection-hero', position: heroRegion.position ?? 0, content: heroContent },
    ...contentSections.map((section, index) => ({ key: section.key, position: section.position ?? index + 2, content: <SectionRenderer layout={{ template: { sections: [section] } }} registry={HOME_SECTION_REGISTRY} context={collectionContext} /> })),
    { key: productsRegion.key || 'collection-products', position: productsRegion.position ?? 1, content: productsContent },
  ].filter(Boolean).sort((left, right) => left.position - right.position);

  return (
    <div className={`category-landing${campaignStyle ? ' category-landing--campaign' : ''}`} data-template={category.themeTemplateKey || 'collection.default'}>
      <SEO
        title={category.name}
        description={category.description || `Browse ${category.name} products`}
        breadcrumbs={[
          { name: t('nav.home', 'Home'), url: '/' },
          { name: t('nav.products', 'Products'), url: '/products' },
          { name: category.name, url: `/category/${category.slug}` },
        ]}
        collection={{
          name: category.name,
          items: products.map(p => ({
            name: p.name,
            slug: p.slug,
            price: p.price,
            image: (p.images && p.images[0]) ? getImageUrl(p.images[0], { size: 'large' }) : null,
          })),
        }}
      />

      {templateRegions.map((region) => <React.Fragment key={region.key}>{region.content}</React.Fragment>)}
    </div>
  );
};

export default CategoryLanding;
